import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FloppyDiskIcon } from '@phosphor-icons/react/dist/csr/FloppyDisk';
import { CheckIcon } from '@phosphor-icons/react/dist/csr/Check';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';

import { Button } from '../../design-system/primitives/Button/Button';
import { Textarea } from '../../design-system/primitives/Textarea/Textarea';
import { Combobox } from '../../design-system/primitives/Combobox/Combobox';
import { Dialog } from '../../design-system/composites/Dialog/Dialog';
import { Input } from '../../design-system/primitives/Input/Input';
import { Select } from '../../design-system/primitives/Select/Select';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
} from '../../lib/api/client';
import { casesApi, type CaseInput, type CaseRow, type CodeClaimResult } from '../../lib/api/cases';
import { deviceModelsApi, type DeviceModel } from '../../lib/api/devices';
import { centresApi, citiesApi, dispensariesApi, type Centre, type City, type Dispensary } from '../../lib/api/reference';
import { referenceName } from '../../lib/format/referenceName';
import { deviceModelLabel } from '../devices/deviceFormHelpers';
import { mapDetails } from '../reference/referenceFormHelpers';
import styles from './CaseFormDialog.module.css';

/**
 * The create-case form (M-06, task 5) — the one the demonstration turns on.
 *
 * ## Dispensary -> city -> centre, cascading through SERVER filters
 *
 * Cities are global since the API's `make_cities_global` migration — one
 * row per real place, no owning dispensary — so "the cities of dispensary X"
 * can no longer be read off a city row client-side (there is no
 * `dispensary_uuid` on it any more). The API derives it instead:
 * `GET /reference/cities?dispensary=<uuid>` answers the cities holding at
 * least one of that dispensary's active centres. Both halves of the cascade
 * are therefore server filters, re-fetched on each choice:
 *
 *   dispensary -> `citiesApi.list(false, dispensary)`
 *   + city     -> `centresApi.list(false, { dispensary, city })`
 *
 * The centre fetch passes BOTH parents, not just the city: a global city
 * holds every dispensary's centres, and filtering by city alone would offer
 * a MOH agent the Nahdi branch down the road. Choosing a different
 * dispensary clears both city and centre — a city or centre left over from
 * the previous dispensary would silently travel with the wrong parent.
 *
 * ## `CaseInput` carries `perception_code`/`assessment_code`/`agent_notes`
 * on the SAME create call (M-06 fix round, gap 1)
 *
 * `StoreCaseRequest::rules()` (verified against the API repository AFTER
 * this fix) now accepts all three, `sometimes`/`nullable` — this form
 * sends whichever of the three the agent filled in on the ONE
 * `casesApi.create()` call, never a create-then-update chain. That chain
 * used to mean two requests where one belonged, a partial-failure path in
 * the middle of the create action, and an audit trail showing a
 * `case.updated` row immediately after every single create — all three
 * gone now that the fields travel on the create request itself.
 *
 * ## Success does not close the dialog
 *
 * `POST /cases` answers ONE `code_claims` ENTRY PER REQUESTED PRODUCT LINE,
 * and an unclaimed line is a NORMAL outcome, not an error (`cases.ts`'s own
 * docblock) — the pool may hold nothing for that dispensary and type right
 * now, or another agent may have taken the last one a moment ago
 * (`SKIP LOCKED` contention, not "out of stock"). A blank where a code
 * should be is exactly what an audience asks about, so this dialog shows a
 * RESULT PANEL naming which lines got a code and which did not (and why),
 * with its own "Done" control — never `onSaved` fired straight from the
 * network response, which would close the dialog before anyone could read
 * the very thing this screen exists to prove.
 *
 * ...UNLESS NOTHING WAS LINKED. The panel lists only the lines that DID get a
 * code. A case where no line claimed one has nothing to report, and a panel
 * of "No free code" rows was a note about an absence the agent never asked
 * about — so in that case the dialog closes straight away, exactly as any
 * other successful save does.
 *
 * ## A serial field per line, shown only when ticked (M-08 Task 2)
 *
 * `reader_serial`/`sensor_serial` (M-06's K-9's missing columns) are each
 * rendered ONLY when that line's own checkbox is ticked — a sensor serial
 * on a reader-only case is a field with nothing to say. Both are optional
 * even when the line is ticked: the legacy has rows with an empty serial,
 * and the API's own rule is `nullable`.
 *
 * ## NO generation field here — `reader_type_code`/`sensor_type_code` IS the generation (fix round 1)
 *
 * A first pass ALSO rendered a `reader_type`/`sensor_type` generation
 * `Select` per line here, alongside this form's existing
 * `reader_type_code`/`sensor_type_code` (which choose which voucher
 * `CodeClaim` claims). Review found the two were never reconciled: a
 * caller could tick `libre_2` in one and `libre_3_plus` in the other, both
 * would validate, and the case would store a device generation different
 * from the one the claimed voucher was actually for. The generation the
 * agent picks to claim a voucher IS the patient's device generation — ONE
 * fact, so ONE field. `StoreCaseRequest` now derives the stored
 * `reader_type`/`sensor_type` from `reader_type_code`/`sensor_type_code`
 * itself (see that class's own docblock); this form sends only the serial,
 * never a second, independent generation. `CaseUpdateDialog` is different:
 * an EDIT claims no voucher, so it keeps a real, correctable generation
 * field — see that dialog's own docblock.
 */

export interface CaseFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: (created: CaseRow, codeClaims: readonly CodeClaimResult[]) => void;
}

type MissingField =
  | 'case_number'
  | 'mobile'
  | 'dispensary_id'
  | 'type'
  | 'reader_type_code'
  | 'sensor_type_code';

interface FormState {
  dispensaryUuid: string | null;
  cityUuid: string | null;
  centerUuid: string | null;
  caseNumber: string;
  mobile: string;
  reader: boolean;
  readerTypeCode: string;
  readerSerial: string;
  sensor: boolean;
  sensorTypeCode: string;
  sensorSerial: string;
  perceptionCode: string;
  assessmentCode: string;
  agentNotes: string;
}

const BLANK: FormState = {
  dispensaryUuid: null,
  cityUuid: null,
  centerUuid: null,
  caseNumber: '',
  mobile: '',
  reader: false,
  readerTypeCode: '',
  readerSerial: '',
  sensor: false,
  sensorTypeCode: '',
  sensorSerial: '',
  perceptionCode: '',
  assessmentCode: '',
  agentNotes: '',
};

const FORBIDDEN_CODE = 'forbidden';

interface CreateResult {
  case: CaseRow;
  codeClaims: readonly CodeClaimResult[];
}

export function CaseFormDialog({ open, onClose, onSaved }: CaseFormDialogProps) {
  const { t, i18n } = useTranslation();

  const [state, setState] = useState<FormState>(BLANK);
  const [missing, setMissing] = useState<readonly MissingField[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [generalFailure, setGeneralFailure] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<CreateResult | null>(null);

  const [dispensaries, setDispensaries] = useState<readonly Dispensary[]>([]);
  const [cities, setCities] = useState<readonly City[]>([]);
  const [centres, setCentres] = useState<readonly Centre[]>([]);
  const [deviceModels, setDeviceModels] = useState<readonly DeviceModel[]>([]);

  const inFlight = useRef(false);
  const caseNumberRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setState(BLANK);
    setMissing([]);
    setFieldErrors({});
    setGeneralFailure(null);
    setResult(null);
  }, [open]);

  // Fetched once per open — the reference catalogue and the device model
  // list, exactly `CentreFormDialog.tsx`/`DeviceFormDialog.tsx`'s own shape
  // for a caller-supplied picker source.
  useEffect(() => {
    if (!open) return;
    void dispensariesApi.list(false).then(setDispensaries);
    void deviceModelsApi.list().then(setDeviceModels);
  }, [open]);

  const loadCities = useCallback(async (dispensaryUuid: string) => {
    const list = await citiesApi.list(false, dispensaryUuid);
    setCities(list);
  }, []);

  const loadCentres = useCallback(async (dispensaryUuid: string, cityUuid: string) => {
    const list = await centresApi.list(false, { dispensary: dispensaryUuid, city: cityUuid });
    setCentres(list);
  }, []);

  // Both re-fetched from the server on each choice — see this file's own
  // docblock for the cascade.
  useEffect(() => {
    if (state.dispensaryUuid === null) {
      setCities([]);
      return;
    }
    void loadCities(state.dispensaryUuid);
  }, [state.dispensaryUuid, loadCities]);

  useEffect(() => {
    if (state.dispensaryUuid === null || state.cityUuid === null) {
      setCentres([]);
      return;
    }
    void loadCentres(state.dispensaryUuid, state.cityUuid);
  }, [state.dispensaryUuid, state.cityUuid, loadCentres]);

  function describeFailure(error: ApiError): string {
    switch (error.code) {
      case FORBIDDEN_CODE:
        return t('cases.notPermitted');
      case TOO_MANY_REQUESTS_CODE:
        return t('cases.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('cases.actionFailed');
    }
  }

  function setDispensary(value: string): void {
    // Clears city and centre — a centre left over from another dispensary's
    // city would silently travel with the wrong parent (this file's own
    // docblock).
    setState((current) => ({ ...current, dispensaryUuid: value, cityUuid: null, centerUuid: null }));
  }

  function setCity(value: string): void {
    setState((current) => ({ ...current, cityUuid: value, centerUuid: null }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (inFlight.current) return;

    const empty: MissingField[] = [];
    if (state.caseNumber.trim() === '') empty.push('case_number');
    if (state.mobile.trim() === '') empty.push('mobile');
    if (state.dispensaryUuid === null || state.dispensaryUuid === '') empty.push('dispensary_id');
    if (!state.reader && !state.sensor) empty.push('type');
    if (state.reader && state.readerTypeCode === '') empty.push('reader_type_code');
    if (state.sensor && state.sensorTypeCode === '') empty.push('sensor_type_code');

    if (empty.length > 0) {
      setMissing(empty);
      return;
    }
    setMissing([]);

    inFlight.current = true;
    setSubmitting(true);
    setFieldErrors({});
    setGeneralFailure(null);

    try {
      const type: Array<'Reader' | 'Sensor'> = [];
      if (state.reader) type.push('Reader');
      if (state.sensor) type.push('Sensor');

      const perceptionCode = state.perceptionCode.trim();
      const assessmentCode = state.assessmentCode.trim();
      const agentNotes = state.agentNotes.trim();

      const input: CaseInput = {
        case_number: state.caseNumber.trim(),
        mobile: state.mobile.trim(),
        dispensary_id: state.dispensaryUuid as string,
        city_id: state.cityUuid ?? undefined,
        center_id: state.centerUuid ?? undefined,
        type,
        reader_type_code: state.reader ? state.readerTypeCode : undefined,
        sensor_type_code: state.sensor ? state.sensorTypeCode : undefined,
        // M-08 Task 2 — a sensor serial on a reader-only case is a field
        // with nothing to say, so each is only sent when its own line's
        // checkbox is ticked (this file's own docblock). NO `reader_type`/
        // `sensor_type` HERE (fix round 1) — the API derives the stored
        // generation from `reader_type_code`/`sensor_type_code` above; see
        // this file's own docblock for why a second field for the same
        // fact was removed.
        reader_serial: state.reader && state.readerSerial.trim() !== '' ? state.readerSerial.trim() : undefined,
        sensor_serial: state.sensor && state.sensorSerial.trim() !== '' ? state.sensorSerial.trim() : undefined,
        perception_code: perceptionCode === '' ? undefined : perceptionCode,
        assessment_code: assessmentCode === '' ? undefined : assessmentCode,
        agent_notes: agentNotes === '' ? undefined : agentNotes,
      };

      const created = await casesApi.create(input);

      const claimed = created.code_claims.filter((claim) => claim.claimed);
      if (claimed.length === 0) {
        onSaved(created.data, created.code_claims);
        return;
      }
      setResult({ case: created.data, codeClaims: claimed });
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      if (Object.keys(error.details).length > 0) {
        setFieldErrors(mapDetails(error.details));
      } else {
        setGeneralFailure(describeFailure(error));
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  function handleDone(): void {
    if (result === null) return;
    onSaved(result.case, result.codeClaims);
  }

  /*
   * EVERY OPTION LABEL IS LOCALE-AWARE. All five of these read `name_en`
   * unconditionally until `translations.php` made `name_en` genuinely
   * English — before that, `cities.name_en` held the ARABIC name, so an
   * Arabic operator got Arabic labels by accident and the bug could not be
   * seen. See `referenceName()`'s own docblock.
   */
  const language = i18n.language;

  const dispensaryOptions = useMemo(
    () => dispensaries.map((d) => ({ value: d.uuid, label: referenceName(d, language) })),
    [dispensaries, language],
  );

  // Already narrowed to the chosen dispensary by the server — see the
  // docblock. Empty until a dispensary is chosen (the effect clears it).
  const cityOptions = useMemo(
    () => cities.map((c) => ({ value: c.uuid, label: referenceName(c, language) })),
    [cities, language],
  );

  const centerOptions = useMemo(
    () => centres.map((c) => ({ value: c.uuid, label: referenceName(c, language) })),
    [centres, language],
  );

  // `deviceModelLabel`, the helper written for exactly this defect in the
  // devices module — these two dropdowns were simply missed by it.
  const readerModelOptions = useMemo(
    () =>
      deviceModels
        .filter((m) => m.kind === 'reader')
        .map((m) => ({ value: m.code, label: deviceModelLabel(m, language) })),
    [deviceModels, language],
  );
  const sensorModelOptions = useMemo(
    () =>
      deviceModels
        .filter((m) => m.kind === 'sensor')
        .map((m) => ({ value: m.code, label: deviceModelLabel(m, language) })),
    [deviceModels, language],
  );

  if (result !== null) {
    return (
      <Dialog
        open={open}
        kind="form"
        onClose={handleDone}
        title={t('cases.createdTitle', { caseNumber: result.case.case_number })}
      >
        <div className={styles['result']}>
          <p className={styles['resultIntro']}>{t('cases.createdIntro')}</p>

          <ul className={styles['claimList']}>
            {result.codeClaims.map((claim) => (
              <li key={`${claim.kind}-${claim.type_code}`} className={styles['claimRow']}>
                <span className={styles['claimKind']}>{t(`codes.kind.${claim.kind}`)}</span>
                <bdi dir="ltr" className={styles['claimCode']} data-testid={`claimCode-${claim.kind}`}>
                  {claim.code}
                </bdi>
              </li>
            ))}
          </ul>

          <div className={styles['actions']}>
            <Button onClick={handleDone} icon={<CheckIcon size={16} />}>
              {t('cases.done')}
            </Button>
          </div>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      kind="form"
      size="lg"
      onClose={onClose}
      initialFocusRef={caseNumberRef}
      title={t('cases.createTitle')}
    >
      <form
        className={styles['form']}
        method="post"
        noValidate
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <div className={styles['failure']} role="alert" aria-atomic="true" data-testid="caseFormFailure">
          {generalFailure}
        </div>

        <Input
          ref={caseNumberRef}
          label={t('table.caseNumber')}
          width="compact"
          dir="ltr"
          required
          value={state.caseNumber}
          error={missing.includes('case_number') ? t('cases.required') : fieldErrors['case_number']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, caseNumber: value }));
          }}
        />

        <Input
          label={t('form.mobileLabel')}
          width="phone"
          dir="ltr"
          placeholder={t('form.mobilePlaceholder')}
          required
          value={state.mobile}
          error={missing.includes('mobile') ? t('cases.required') : fieldErrors['mobile']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, mobile: value }));
          }}
        />

        <Combobox
          label={t('cases.fieldDispensary')}
          width="fill"
          required
          options={dispensaryOptions}
          value={state.dispensaryUuid}
          emptyMessage={t('cases.noDispensariesYet')}
          error={missing.includes('dispensary_id') ? t('cases.required') : fieldErrors['dispensary_id']}
          onChange={setDispensary}
        />

        <Combobox
          label={t('cases.fieldCity')}
          width="fill"
          options={cityOptions}
          value={state.cityUuid}
          emptyMessage={t('cases.noCitiesYet')}
          disabled={state.dispensaryUuid === null}
          error={fieldErrors['city_id']}
          onChange={setCity}
        />

        <Combobox
          label={t('cases.fieldCentre')}
          width="fill"
          options={centerOptions}
          value={state.centerUuid}
          emptyMessage={t('cases.noCentresYet')}
          disabled={state.cityUuid === null}
          error={fieldErrors['center_id']}
          onChange={(value) => {
            setState((current) => ({ ...current, centerUuid: value }));
          }}
        />

        <fieldset className={styles['lines']}>
          <legend className={styles['linesLegend']}>{t('cases.fieldProductLines')}</legend>

          <label className={styles['checkboxRow']}>
            <input
              type="checkbox"
              className={styles['checkbox']}
              checked={state.reader}
              onChange={(event) => {
                const { checked } = event.target;
                setState((current) => ({ ...current, reader: checked, readerTypeCode: checked ? current.readerTypeCode : '' }));
              }}
            />
            {t('codes.kind.reader')}
          </label>

          {state.reader ? (
            <Select
              label={t('cases.fieldReaderType')}
              width="compact"
              required
              placeholder={t('cases.chooseType')}
              options={readerModelOptions}
              value={state.readerTypeCode}
              error={missing.includes('reader_type_code') ? t('cases.required') : undefined}
              onChange={(event) => {
                const { value } = event.target;
                setState((current) => ({ ...current, readerTypeCode: value }));
              }}
            />
          ) : null}

          {state.reader ? (
            <Input
              label={t('cases.readerSerial')}
              width="compact"
              dir="ltr"
              value={state.readerSerial}
              error={fieldErrors['reader_serial']}
              onChange={(event) => {
                const { value } = event.target;
                setState((current) => ({ ...current, readerSerial: value }));
              }}
            />
          ) : null}

          <label className={styles['checkboxRow']}>
            <input
              type="checkbox"
              className={styles['checkbox']}
              checked={state.sensor}
              onChange={(event) => {
                const { checked } = event.target;
                setState((current) => ({ ...current, sensor: checked, sensorTypeCode: checked ? current.sensorTypeCode : '' }));
              }}
            />
            {t('codes.kind.sensor')}
          </label>

          {state.sensor ? (
            <Select
              label={t('cases.fieldSensorType')}
              width="compact"
              required
              placeholder={t('cases.chooseType')}
              options={sensorModelOptions}
              value={state.sensorTypeCode}
              error={missing.includes('sensor_type_code') ? t('cases.required') : undefined}
              onChange={(event) => {
                const { value } = event.target;
                setState((current) => ({ ...current, sensorTypeCode: value }));
              }}
            />
          ) : null}

          {state.sensor ? (
            <Input
              label={t('cases.sensorSerial')}
              width="compact"
              dir="ltr"
              value={state.sensorSerial}
              error={fieldErrors['sensor_serial']}
              onChange={(event) => {
                const { value } = event.target;
                setState((current) => ({ ...current, sensorSerial: value }));
              }}
            />
          ) : null}

          {missing.includes('type') ? (
            <p className={styles['inlineError']} role="alert">
              {t('cases.pickOneLine')}
            </p>
          ) : null}
        </fieldset>

        <Input
          label={t('cases.fieldPerceptionCode')}
          width="compact"
          dir="ltr"
          value={state.perceptionCode}
          error={fieldErrors['perception_code']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, perceptionCode: value }));
          }}
        />

        <Input
          label={t('cases.fieldAssessmentCode')}
          width="compact"
          dir="ltr"
          value={state.assessmentCode}
          error={fieldErrors['assessment_code']}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, assessmentCode: value }));
          }}
        />

        <Textarea
          label={t('cases.fieldAgentNotes')}
          className={styles['wide']}
          dir="auto"
          value={state.agentNotes}
          onChange={(event) => {
            const { value } = event.target;
            setState((current) => ({ ...current, agentNotes: value }));
          }}
        />

        <div className={styles['actions']}>
          <Button type="submit" loading={submitting}
            icon={<FloppyDiskIcon size={16} />}
          >
            {t('actions.saveCase')}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose} icon={<XIcon size={16} />}>
            {t('cases.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
